import {
  normalizeTemplateName,
  validateTemplateMetadata
} from "./template-contracts.js";

let templateControllerSequence = 0;

export class TemplateMenuController {
  constructor({
    trigger,
    container,
    repository,
    typeLabel,
    capturePayload,
    applyTemplate,
    restoreDefaults,
    applicationWarning = null,
    restoreMessage,
    announce = null,
    menuLayout = "actions"
  }) {
    if (!(trigger instanceof HTMLButtonElement)) {
      throw new TypeError("Template menu requires a button trigger.");
    }
    this.trigger = trigger;
    this.control = trigger.closest("[data-template-control]") || trigger.parentElement;
    this.container = container;
    this.repository = repository;
    this.typeLabel = typeLabel;
    this.capturePayload = capturePayload;
    this.applyTemplate = applyTemplate;
    this.restoreDefaults = restoreDefaults;
    this.applicationWarning = applicationWarning;
    this.restoreMessage = restoreMessage;
    this.announce = announce;
    this.menuLayout = menuLayout;
    this.dialogId = ++templateControllerSequence;
    this.summaries = [];
    this.returnFocus = null;
    this.confirmReturnFocus = null;
    this.reopenManage = false;
    this.editorContext = null;
    this.confirmResolver = null;

    this.menu = buildMenu(typeLabel, menuLayout);
    this.control.append(this.menu);
    this.editor = buildEditor(typeLabel, this.dialogId);
    this.manager = buildManager(typeLabel, this.dialogId);
    this.confirm = buildConfirm(this.dialogId);
    this.container.append(this.editor, this.manager, this.confirm);

    this.onDocumentPointerDown = (event) => {
      if (
        !this.menu.hidden
        && event.target instanceof Node
        && !this.control.contains(event.target)
      ) this.closeMenu();
    };
    document.addEventListener("pointerdown", this.onDocumentPointerDown);
    this.trigger.addEventListener("click", () => this.toggleMenu());
    this.trigger.addEventListener("keydown", (event) => {
      if (["ArrowDown", "Enter", " "].includes(event.key)) {
        event.preventDefault();
        this.openMenu();
      }
    });
    this.menu.addEventListener("click", (event) => this.handleMenuClick(event));
    this.menu.addEventListener("keydown", (event) => this.handleMenuKeydown(event));
    this.editor.addEventListener("click", (event) => this.handleEditorClick(event));
    this.manager.addEventListener("click", (event) => this.handleManagerClick(event));
    this.manager.addEventListener("input", () => this.renderManager());
    this.confirm.addEventListener("click", (event) => this.handleConfirmClick(event));
    for (const overlay of [this.editor, this.manager, this.confirm]) {
      overlay.addEventListener("keydown", (event) => this.handleDialogKeydown(overlay, event));
      overlay.addEventListener("pointerdown", (event) => event.stopPropagation());
    }
    void this.refresh();
  }

  async refresh() {
    try {
      this.summaries = await this.repository.list();
      this.renderMenu();
      if (!this.manager.hidden) this.renderManager();
    } catch (error) {
      this.summaries = [];
      this.renderMenu(error?.message || "Templates could not be loaded.");
    }
  }

  async openMenu() {
    await this.refresh();
    this.menu.hidden = false;
    this.trigger.setAttribute("aria-expanded", "true");
    focusable(this.menu)[0]?.focus();
  }

  closeMenu({ focus = false } = {}) {
    this.menu.hidden = true;
    this.trigger.setAttribute("aria-expanded", "false");
    const applySubmenu = this.menu.querySelector("[data-template-apply-submenu]");
    const applyToggle = this.menu.querySelector("[data-template-apply-toggle]");
    if (applySubmenu) applySubmenu.hidden = true;
    applyToggle?.setAttribute("aria-expanded", "false");
    if (focus) this.trigger.focus({ preventScroll: true });
  }

  toggleMenu() {
    if (this.menu.hidden) void this.openMenu();
    else this.closeMenu({ focus: true });
  }

  renderMenu(error = "") {
    const submenu = this.menu.querySelector(
      this.menuLayout === "direct-list"
        ? "[data-template-list]"
        : "[data-template-apply-submenu]"
    );
    if (!submenu) return;
    if (this.menuLayout === "direct-list") {
      submenu.replaceChildren();
      if (error) {
        submenu.append(menuStatus(error, false));
      } else if (!this.summaries.length) {
        submenu.append(menuStatus("No saved templates", true));
      } else {
        for (const item of this.summaries) submenu.append(templateMenuRow(item));
      }
      return;
    }
    const recents = this.repository.recent(this.summaries);
    submenu.replaceChildren();
    if (error) {
      submenu.append(menuStatus(error, false));
    } else if (!this.summaries.length) {
      submenu.append(menuStatus("No saved templates", true));
    } else {
      if (recents.length) {
        const label = document.createElement("span");
        label.className = "template-menu-label";
        label.textContent = "Recently used";
        submenu.append(label);
        for (const item of recents) submenu.append(templateMenuButton(item));
        submenu.append(separator());
      }
      const all = document.createElement("button");
      all.type = "button";
      all.role = "menuitem";
      all.dataset.templateAction = "manage";
      all.textContent = "All templates…";
      submenu.append(all);
    }
  }

  async handleMenuClick(event) {
    const button = event.target instanceof Element
      ? event.target.closest("button")
      : null;
    if (!(button instanceof HTMLButtonElement)) return;
    const action = button.dataset.templateAction;
    if (action === "apply-toggle") {
      const submenu = this.menu.querySelector("[data-template-apply-submenu]");
      submenu.hidden = !submenu.hidden;
      button.setAttribute("aria-expanded", String(!submenu.hidden));
      if (!submenu.hidden) focusable(submenu)[0]?.focus();
      return;
    }
    this.closeMenu();
    if (action === "save") this.openEditor({ mode: "save" });
    if (action === "manage") this.openManager();
    if (action === "restore") await this.confirmRestore();
    if (action === "delete-direct") {
      const summary = this.summaries.find((item) => item.id === button.dataset.templateId);
      if (summary) await this.deleteTemplate(summary, { returnTo: this.trigger });
      return;
    }
    if (button.dataset.templateId) await this.previewAndApply(button.dataset.templateId);
  }

  handleMenuKeydown(event) {
    if (event.key === "Escape" || event.key === "ArrowLeft") {
      event.preventDefault();
      event.stopPropagation();
      this.closeMenu({ focus: true });
      return;
    }
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const buttons = focusable(this.menu);
    const current = buttons.indexOf(document.activeElement);
    const next = event.key === "Home"
      ? 0
      : event.key === "End"
        ? buttons.length - 1
        : event.key === "ArrowDown"
          ? (current + 1 + buttons.length) % buttons.length
          : (current - 1 + buttons.length) % buttons.length;
    buttons[next]?.focus();
  }

  openEditor({ mode, record = null }) {
    this.closeMenu();
    this.returnFocus = this.control.contains(document.activeElement)
      ? this.trigger
      : document.activeElement;
    this.editorContext = { mode, record };
    const form = this.editor.querySelector("form");
    const title = this.editor.querySelector("[data-template-editor-title]");
    const name = form.elements.namedItem("name");
    const description = form.elements.namedItem("description");
    const submit = form.querySelector("[data-template-editor-action='submit']");
    const labels = {
      save: [`Save ${this.typeLabel.toLowerCase()} template`, "Save"],
      rename: ["Rename template", "Save"],
      description: ["Edit template description", "Save"],
      duplicate: ["Duplicate template", "Duplicate"]
    };
    [title.textContent, submit.textContent] = labels[mode];
    name.value = mode === "duplicate" ? `Copy of ${record.name}` : record?.name || "";
    description.value = record?.description || "";
    name.readOnly = mode === "description";
    this.showEditorError("");
    this.editor.hidden = false;
    (mode === "description" ? description : name).focus({ preventScroll: true });
    if (mode !== "description") name.select();
  }

  closeEditor({ restoreFocus = true } = {}) {
    this.editor.hidden = true;
    this.editorContext = null;
    if (this.reopenManage) {
      this.reopenManage = false;
      this.openManager({ preserveFocus: true });
    } else if (restoreFocus) {
      this.returnFocus?.focus?.({ preventScroll: true });
    }
  }

  async submitEditor(event) {
    event?.preventDefault?.();
    const form = this.editor.querySelector("form");
    const submit = form.querySelector("[data-template-editor-action='submit']");
    const name = form.elements.namedItem("name").value;
    const description = form.elements.namedItem("description").value;
    let metadata;
    try {
      metadata = validateTemplateMetadata(name, description);
    } catch (error) {
      this.showEditorError(error.message);
      return;
    }
    submit.disabled = true;
    try {
      const context = this.editorContext;
      let saved;
      if (context.mode === "save") {
        const payload = this.capturePayload();
        try {
          saved = await this.repository.create({ ...metadata, payload });
        } catch (error) {
          if (error.code !== "DuplicateName") throw error;
          await this.refresh();
          const existing = this.summaries.find(
            (item) => normalizeTemplateName(item.name) === metadata.normalizedName
          );
          if (!existing) throw error;
          const replace = await this.askConfirm({
            title: "Replace existing template?",
            body: `"${existing.name}" already exists. Replace its saved ${this.typeLabel.toLowerCase()} configuration?`,
            actionLabel: "Replace template",
            destructive: true,
            returnTo: this.editor
          });
          if (!replace) return;
          saved = await this.repository.replacePayload(existing.id, {
            payload,
            expectedVersion: existing.version
          });
          if ((saved.description || "") !== (metadata.description || "")) {
            saved = await this.repository.updateMetadata(saved.id, {
              name: saved.name,
              description: metadata.description,
              expectedVersion: saved.version
            });
          }
        }
      } else if (context.mode === "duplicate") {
        saved = await this.repository.duplicate(context.record.id, {
          ...metadata,
          expectedVersion: context.record.version
        });
      } else {
        saved = await this.repository.updateMetadata(context.record.id, {
          ...metadata,
          expectedVersion: context.record.version
        });
      }
      if (context.mode === "save") this.repository.markUsed(saved.id);
      await this.refresh();
      this.closeEditor();
      this.announce?.(
        context.mode === "save"
          ? `${this.typeLabel} template "${saved.name}" saved.`
          : `Template "${saved.name}" updated.`
      );
    } catch (error) {
      this.showEditorError(actionableError(error));
    } finally {
      submit.disabled = false;
    }
  }

  handleEditorClick(event) {
    const action = event.target instanceof Element
      ? event.target.closest("[data-template-editor-action]")?.dataset.templateEditorAction
      : null;
    if (action === "cancel") this.closeEditor();
    if (action === "submit") void this.submitEditor(event);
  }

  openManager({ preserveFocus = false } = {}) {
    this.closeMenu();
    if (!preserveFocus) {
      this.returnFocus = this.control.contains(document.activeElement)
        ? this.trigger
        : document.activeElement;
    }
    this.manager.hidden = false;
    this.renderManager();
    const search = this.manager.querySelector("[data-template-search]");
    search.focus({ preventScroll: true });
  }

  closeManager({ restoreFocus = true } = {}) {
    this.manager.hidden = true;
    if (restoreFocus) this.returnFocus?.focus?.({ preventScroll: true });
  }

  renderManager() {
    const query = this.manager.querySelector("[data-template-search]")
      .value.trim().toLocaleLowerCase();
    const list = this.manager.querySelector("[data-template-manager-list]");
    const items = this.summaries.filter((item) =>
      !query
      || item.name.toLocaleLowerCase().includes(query)
      || String(item.description || "").toLocaleLowerCase().includes(query)
    );
    list.replaceChildren();
    if (!items.length) {
      const empty = document.createElement("p");
      empty.className = "template-manager-empty";
      empty.textContent = this.summaries.length
        ? "No templates match this search."
        : `No saved ${this.typeLabel.toLowerCase()} templates.`;
      list.append(empty);
      return;
    }
    for (const item of items) list.append(managerRow(item, this.typeLabel));
  }

  async handleManagerClick(event) {
    const button = event.target instanceof Element
      ? event.target.closest("button")
      : null;
    if (!(button instanceof HTMLButtonElement)) return;
    const action = button.dataset.templateManagerAction;
    if (action === "close") {
      this.closeManager();
      return;
    }
    const summary = this.summaries.find((item) => item.id === button.dataset.templateId);
    if (!summary) return;
    if (action === "apply") {
      this.closeManager({ restoreFocus: false });
      await this.previewAndApply(summary.id);
      return;
    }
    if (["rename", "description", "duplicate"].includes(action)) {
      const record = await this.loadRecord(summary.id);
      if (!record) return;
      this.closeManager({ restoreFocus: false });
      this.reopenManage = true;
      this.openEditor({ mode: action, record });
      return;
    }
    if (action === "delete") await this.deleteTemplate(summary);
  }

  async previewAndApply(id) {
    const record = await this.loadRecord(id);
    if (!record) return;
    let warning = "";
    try {
      warning = this.applicationWarning?.(record.payload) || "";
    } catch (error) {
      this.announce?.(actionableError(error));
      return;
    }
    const body = [
      record.description || `Apply the saved ${this.typeLabel.toLowerCase()} configuration.`,
      warning
    ].filter(Boolean).join("\n\n");
    const accepted = await this.askConfirm({
      title: `Apply "${record.name}"?`,
      body,
      actionLabel: "Apply template"
    });
    if (!accepted) return;
    try {
      await this.applyTemplate(record);
      this.repository.markUsed(record.id);
      await this.refresh();
      this.announce?.(`${this.typeLabel} template "${record.name}" applied.`);
    } catch (error) {
      this.announce?.(actionableError(error));
    }
  }

  async confirmRestore() {
    const accepted = await this.askConfirm({
      title: `Restore ${this.typeLabel.toLowerCase()} defaults?`,
      body: this.restoreMessage,
      actionLabel: "Restore defaults",
      destructive: true
    });
    if (!accepted) return;
    try {
      await this.restoreDefaults();
      this.announce?.(`${this.typeLabel} defaults restored. Saved templates were not changed.`);
    } catch (error) {
      this.announce?.(actionableError(error));
    }
  }

  async deleteTemplate(summary, { returnTo = this.manager } = {}) {
    const accepted = await this.askConfirm({
      title: `Delete "${summary.name}"?`,
      body: "This deletes only the saved template. Charts that previously used it will not change.",
      actionLabel: "Delete template",
      destructive: true,
      returnTo
    });
    if (!accepted) return;
    try {
      await this.repository.delete(summary.id, summary.version);
      await this.refresh();
      this.announce?.(`Template "${summary.name}" deleted.`);
    } catch (error) {
      this.announce?.(actionableError(error));
      await this.refresh();
    }
  }

  async loadRecord(id) {
    try {
      return await this.repository.get(id);
    } catch (error) {
      this.announce?.(actionableError(error));
      await this.refresh();
      return null;
    }
  }

  askConfirm({
    title,
    body,
    actionLabel,
    destructive = false,
    returnTo = null
  }) {
    if (this.confirmResolver) this.confirmResolver(false);
    this.confirmReturnFocus = returnTo || document.activeElement;
    this.confirm.querySelector("[data-template-confirm-title]").textContent = title;
    const bodyElement = this.confirm.querySelector("[data-template-confirm-body]");
    bodyElement.replaceChildren(...String(body).split("\n\n").map((paragraph) => {
      const element = document.createElement("p");
      element.textContent = paragraph;
      return element;
    }));
    const accept = this.confirm.querySelector("[data-template-confirm-accept]");
    accept.textContent = actionLabel;
    accept.classList.toggle("is-destructive", destructive);
    this.confirm.hidden = false;
    accept.focus({ preventScroll: true });
    return new Promise((resolve) => {
      this.confirmResolver = resolve;
    });
  }

  handleConfirmClick(event) {
    const action = event.target instanceof Element
      ? event.target.closest("[data-template-confirm-action]")?.dataset.templateConfirmAction
      : null;
    if (action === "accept") this.closeConfirm(true);
    if (action === "cancel") this.closeConfirm(false);
  }

  closeConfirm(result) {
    this.confirm.hidden = true;
    const resolver = this.confirmResolver;
    this.confirmResolver = null;
    resolver?.(result);
    if (!result) this.confirmReturnFocus?.focus?.({ preventScroll: true });
    this.confirmReturnFocus = null;
  }

  handleDialogKeydown(overlay, event) {
    event.stopPropagation();
    if (
      overlay === this.editor
      && event.key === "Enter"
      && event.target instanceof HTMLInputElement
    ) {
      event.preventDefault();
      void this.submitEditor(event);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      if (overlay === this.editor) this.closeEditor();
      if (overlay === this.manager) this.closeManager();
      if (overlay === this.confirm) this.closeConfirm(false);
      return;
    }
    if (event.key !== "Tab") return;
    const items = focusable(overlay);
    if (!items.length) return;
    const first = items[0];
    const last = items.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  showEditorError(message) {
    const error = this.editor.querySelector("[data-template-editor-error]");
    error.textContent = message;
    error.hidden = !message;
  }

  destroy() {
    document.removeEventListener("pointerdown", this.onDocumentPointerDown);
    this.menu.remove();
    this.editor.remove();
    this.manager.remove();
    this.confirm.remove();
  }

  dismiss() {
    this.closeMenu();
    this.editor.hidden = true;
    this.manager.hidden = true;
    if (!this.confirm.hidden) this.closeConfirm(false);
  }
}

function buildMenu(typeLabel, menuLayout) {
  const menu = document.createElement("div");
  menu.className = "template-menu";
  menu.role = "menu";
  menu.ariaLabel = `${typeLabel} templates`;
  menu.hidden = true;
  if (menuLayout === "direct-list") {
    menu.innerHTML = `
      <button type="button" role="menuitem" data-template-action="save">
        Save as new template…
      </button>
      <button type="button" role="menuitem" data-template-action="restore">
        Restore Defaults
      </button>
      <div class="template-menu-separator" role="separator"></div>
      <div data-template-list></div>`;
    return menu;
  }
  menu.innerHTML = `
    <button type="button" role="menuitem" data-template-action="save">
      Save as new template…
    </button>
    <button type="button"
            role="menuitem"
            aria-haspopup="menu"
            aria-expanded="false"
            data-template-action="apply-toggle"
            data-template-apply-toggle>
      <span>Apply template</span><span aria-hidden="true">▸</span>
    </button>
    <div class="template-apply-submenu"
         role="menu"
         aria-label="Apply template"
         data-template-apply-submenu
         hidden></div>
    <div class="template-menu-separator" role="separator"></div>
    <button type="button" role="menuitem" data-template-action="manage">
      Manage templates…
    </button>
    <button type="button" role="menuitem" data-template-action="restore">
      Restore defaults
    </button>`;
  return menu;
}

function buildEditor(typeLabel, dialogId) {
  const titleId = `template-editor-title-${dialogId}`;
  const overlay = document.createElement("div");
  overlay.className = "template-dialog-layer";
  overlay.hidden = true;
  overlay.innerHTML = `
    <section class="template-dialog template-editor-dialog"
             role="dialog"
             aria-modal="true"
             aria-labelledby="${titleId}">
      <form novalidate>
        <header>
          <h2 id="${titleId}" data-template-editor-title>
            Save ${escapeHtml(typeLabel.toLowerCase())} template
          </h2>
        </header>
        <label>
          <span>Template name</span>
          <input name="name" maxlength="120" required autocomplete="off" />
        </label>
        <label>
          <span>Description <small>Optional</small></span>
          <textarea name="description" maxlength="500" rows="3"></textarea>
        </label>
        <p class="template-dialog-error"
           data-template-editor-error
           role="alert"
           hidden></p>
        <footer>
          <button type="button" data-template-editor-action="cancel">Cancel</button>
          <button type="button"
                  class="is-primary"
                  data-template-editor-action="submit">Save</button>
        </footer>
      </form>
    </section>`;
  return overlay;
}

function buildManager(typeLabel, dialogId) {
  const titleId = `template-manager-title-${dialogId}`;
  const overlay = document.createElement("div");
  overlay.className = "template-dialog-layer";
  overlay.hidden = true;
  overlay.innerHTML = `
    <section class="template-dialog template-manager-dialog"
             role="dialog"
             aria-modal="true"
             aria-labelledby="${titleId}">
      <header>
        <div>
          <h2 id="${titleId}">Manage templates</h2>
          <p>Saved ${escapeHtml(typeLabel.toLowerCase())} templates</p>
        </div>
        <button type="button"
                class="template-dialog-close"
                data-template-manager-action="close"
                aria-label="Close template manager">×</button>
      </header>
      <label class="template-manager-search">
        <span class="chart-sr-only">Search templates</span>
        <input type="search"
               data-template-search
               placeholder="Search templates"
               autocomplete="off" />
      </label>
      <div class="template-manager-list" data-template-manager-list></div>
      <footer>
        <button type="button" data-template-manager-action="close">Done</button>
      </footer>
    </section>`;
  return overlay;
}

function buildConfirm(dialogId) {
  const titleId = `template-confirm-title-${dialogId}`;
  const bodyId = `template-confirm-body-${dialogId}`;
  const overlay = document.createElement("div");
  overlay.className = "template-dialog-layer template-confirm-layer";
  overlay.hidden = true;
  overlay.innerHTML = `
    <section class="template-dialog template-confirm-dialog"
             role="alertdialog"
             aria-modal="true"
             aria-labelledby="${titleId}"
             aria-describedby="${bodyId}">
      <header><h2 id="${titleId}" data-template-confirm-title></h2></header>
      <div id="${bodyId}" data-template-confirm-body></div>
      <footer>
        <button type="button"
                data-template-confirm-action="cancel">Cancel</button>
        <button type="button"
                class="is-primary"
                data-template-confirm-action="accept"
                data-template-confirm-accept>Continue</button>
      </footer>
    </section>`;
  return overlay;
}

function managerRow(item, typeLabel) {
  const row = document.createElement("article");
  row.className = "template-manager-row";
  row.innerHTML = `
    <div class="template-manager-summary">
      <strong></strong>
      <span class="template-type-badge"></span>
      <p></p>
      <time></time>
    </div>
    <div class="template-manager-actions">
      <button type="button" data-template-manager-action="apply">Apply</button>
      <button type="button" data-template-manager-action="rename">Rename</button>
      <button type="button" data-template-manager-action="description">Description</button>
      <button type="button" data-template-manager-action="duplicate">Duplicate</button>
      <button type="button" class="is-destructive" data-template-manager-action="delete">
        Delete
      </button>
    </div>`;
  row.querySelector("strong").textContent = item.name;
  row.querySelector(".template-type-badge").textContent = typeLabel;
  const description = row.querySelector("p");
  description.textContent = item.description || "No description";
  const time = row.querySelector("time");
  const date = new Date(item.updatedAtUtc);
  time.dateTime = item.updatedAtUtc;
  time.textContent = `Modified ${Number.isNaN(date.valueOf())
    ? "recently"
    : date.toLocaleDateString(undefined, { dateStyle: "medium" })}`;
  for (const button of row.querySelectorAll("button")) button.dataset.templateId = item.id;
  return row;
}

function templateMenuButton(item) {
  const button = document.createElement("button");
  button.type = "button";
  button.role = "menuitem";
  button.dataset.templateId = item.id;
  button.textContent = item.name;
  if (item.description) button.title = item.description;
  return button;
}

function templateMenuRow(item) {
  const row = document.createElement("div");
  row.className = "template-menu-row";

  const apply = templateMenuButton(item);
  apply.classList.add("template-menu-row-apply");

  const remove = document.createElement("button");
  remove.type = "button";
  remove.role = "menuitem";
  remove.className = "template-menu-row-delete";
  remove.dataset.templateAction = "delete-direct";
  remove.dataset.templateId = item.id;
  remove.ariaLabel = `Delete ${item.name}`;
  remove.title = `Delete ${item.name}`;
  remove.innerHTML = `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5"></path>
    </svg>`;

  row.append(apply, remove);
  return row;
}

function menuStatus(message, disabled) {
  const status = document.createElement("span");
  status.className = "template-menu-status";
  status.role = "menuitem";
  status.ariaDisabled = String(disabled);
  status.title = disabled ? "Save a template to enable this menu." : message;
  status.textContent = message;
  return status;
}

function separator() {
  const element = document.createElement("div");
  element.className = "template-menu-separator";
  element.role = "separator";
  return element;
}

function focusable(scope) {
  return [...scope.querySelectorAll(
    "button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"
  )].filter((element) => !element.hidden && !element.closest("[hidden]"));
}

function actionableError(error) {
  if (error?.code === "ConcurrencyConflict") {
    return "This template changed in another session. Reopen the manager to load the latest version.";
  }
  if (error?.code === "UnsupportedSchemaVersion") {
    return "This template was saved by a newer FlexCharting version and was not applied.";
  }
  return error?.message || "The template action could not be completed.";
}

function escapeHtml(value) {
  const element = document.createElement("span");
  element.textContent = value;
  return element.innerHTML;
}
